/**
 * Vendor portal — Products list.
 * Lists every product owned by the current vendor with status filter and quick actions.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useVendor } from "@/lib/store/useVendor";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { formatPrice } from "@/store/storeCart";
import { Plus, Archive, Eye, EyeOff, Pencil, Search } from "lucide-react";

type Product = {
  id: string;
  slug: string;
  title: string;
  status: string;
  base_price_cents: number;
  product_type: string;
  images: string[];
  view_count: number;
  purchase_count: number;
  created_at: string;
};

const STATUSES = ["all", "active", "draft", "pending_review", "paused", "archived"] as const;
type StatusFilter = (typeof STATUSES)[number];

const VendorProducts = () => {
  const { vendor } = useVendor();
  const { toast } = useToast();
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<StatusFilter>("all");
  const [q, setQ] = useState("");

  const fetchProducts = async () => {
    if (!vendor) return;
    setLoading(true);
    const { data } = await supabase
      .from("store_products")
      .select(
        "id, slug, title, status, base_price_cents, product_type, images, view_count, purchase_count, created_at",
      )
      .eq("vendor_id", vendor.id)
      .order("created_at", { ascending: false });
    setProducts((data as Product[]) ?? []);
    setLoading(false);
  };

  useEffect(() => {
    void fetchProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vendor]);

  const filtered = useMemo(() => {
    return products
      .filter((p) => filter === "all" || p.status === filter)
      .filter((p) => !q || p.title.toLowerCase().includes(q.toLowerCase()));
  }, [products, filter, q]);

  const updateStatus = async (id: string, status: "active" | "paused" | "archived" | "draft") => {
    const updates: Record<string, unknown> = { status };
    if (status === "active") updates.published_at = new Date().toISOString();
    if (status === "archived") updates.archived_at = new Date().toISOString();
    const { error } = await supabase
      .from("store_products")
      .update(updates as never)
      .eq("id", id);
    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } else {
      toast({ title: "Updated", description: `Product set to ${status.replace(/_/g, " ")}.` });
      void fetchProducts();
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-lg font-semibold">Products</h2>
          <p className="text-sm text-muted-foreground">Manage your catalog.</p>
        </div>
        <Button asChild>
          <Link to="/store/vendor/products/new">
            <Plus className="mr-2 h-4 w-4" /> New product
          </Link>
        </Button>
      </div>

      <Card className="p-3 flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by title…"
            className="pl-8"
          />
        </div>
        <div className="flex gap-1 flex-wrap">
          {STATUSES.map((s) => (
            <Button
              key={s}
              size="sm"
              variant={filter === s ? "default" : "outline"}
              onClick={() => setFilter(s)}
              className="capitalize"
            >
              {s.replace(/_/g, " ")}
            </Button>
          ))}
        </div>
      </Card>

      {loading ? (
        <div className="space-y-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-20" />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <Card className="p-12 text-center">
          <p className="text-muted-foreground">
            {products.length === 0
              ? "No products yet. Create your first one!"
              : "No products match your filters."}
          </p>
        </Card>
      ) : (
        <div className="space-y-2">
          {filtered.map((p) => (
            <Card
              key={p.id}
              className="p-3 flex items-center gap-3 hover:bg-muted/20 transition-colors"
            >
              <div className="h-14 w-14 rounded-md bg-muted overflow-hidden shrink-0">
                {p.images?.[0] ? (
                  <img src={p.images[0]} alt="" className="h-full w-full object-cover" />
                ) : (
                  <div className="h-full w-full flex items-center justify-center text-xs text-muted-foreground">
                    No img
                  </div>
                )}
              </div>
              <div className="min-w-0 flex-1">
                <Link
                  to={`/store/vendor/products/${p.id}`}
                  className="font-medium truncate hover:underline block"
                >
                  {p.title}
                </Link>
                <div className="flex items-center gap-2 text-xs text-muted-foreground mt-0.5">
                  <Badge variant="secondary" className="capitalize">
                    {p.status.replace(/_/g, " ")}
                  </Badge>
                  <span className="capitalize">{p.product_type}</span>
                  <span>·</span>
                  <span>{p.view_count} views</span>
                  <span>·</span>
                  <span>{p.purchase_count} sold</span>
                </div>
              </div>
              <div className="font-semibold whitespace-nowrap">
                {formatPrice(p.base_price_cents)}
              </div>
              <div className="flex gap-1 shrink-0">
                <Button asChild size="icon" variant="ghost" title="Edit">
                  <Link to={`/store/vendor/products/${p.id}`}>
                    <Pencil className="h-4 w-4" />
                  </Link>
                </Button>
                {p.status === "active" ? (
                  <Button
                    size="icon"
                    variant="ghost"
                    title="Pause"
                    onClick={() => updateStatus(p.id, "paused")}
                  >
                    <EyeOff className="h-4 w-4" />
                  </Button>
                ) : p.status === "paused" || p.status === "draft" ? (
                  <Button
                    size="icon"
                    variant="ghost"
                    title="Publish"
                    onClick={() => updateStatus(p.id, "active")}
                  >
                    <Eye className="h-4 w-4" />
                  </Button>
                ) : null}
                {p.status !== "archived" && (
                  <Button
                    size="icon"
                    variant="ghost"
                    title="Archive"
                    onClick={() => updateStatus(p.id, "archived")}
                  >
                    <Archive className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default VendorProducts;
